// Form types and their submission policy.
//
// This mirrors the server-side rule in PharmD_System/routers/forms.py
// (submit_form / EVALUATION_FORM_TYPES) — the UI only reflects the policy,
// the backend is what actually enforces it.
//
//   assignment ......... may opt into multiple submissions
//   attendance ......... always multiple (students log past attendance repeatedly)
//   every evaluation ... exactly one submission per target
export const FORM_TYPES = [
    {
        value: 'assignment',
        label: 'Assignment',
        multiple: 'optional',
        audience: 'student',
        hint: 'Assignments are the only type that can accept more than one submission per person.'
    },
    {
        value: 'attendance',
        label: 'Attendance',
        multiple: 'always',
        audience: 'student',
        hint: 'Attendance is always multi-submission — students can keep logging days while the form is open.'
    },
    {
        value: 'studenttopreceptorevaluation',
        label: 'Student to Preceptor Evaluation',
        multiple: 'never',
        audience: 'student',
        hint: 'Evaluations accept a single submission per preceptor being evaluated.'
    },
    {
        value: 'preceptortostudentevaluation',
        label: 'Preceptor to Student Evaluation',
        multiple: 'never',
        audience: 'preceptor',
        hint: 'Evaluations accept a single submission per student being evaluated.'
    },
    {
        value: 'assignmentevaluation',
        label: 'Assignment Evaluation',
        multiple: 'never',
        audience: 'preceptor',
        hint: 'Assignment evaluations accept a single submission per assignment, and appear in the evaluation reports.'
    }
];

const FALLBACK_TYPE = FORM_TYPES[0];

export const getFormTypeRule = (formType) =>
    FORM_TYPES.find((entry) => entry.value === formType) || FALLBACK_TYPE;

export const getFormTypeLabel = (formType) =>
    FORM_TYPES.find((entry) => entry.value === formType)?.label || formType || 'Unknown';

export const EVALUATION_FORM_TYPES = FORM_TYPES
    .filter((entry) => entry.value.includes('evaluation'))
    .map((entry) => entry.value);

export const isEvaluationFormType = (formType) => EVALUATION_FORM_TYPES.includes(formType);

/**
 * Resolve what `allow_multiple_submissions` must be for a given form type.
 * Only assignment forms let the admin choose; everything else is forced.
 */
export const resolveMultipleSubmissions = (formType, currentValue) => {
    const rule = getFormTypeRule(formType);
    if (rule.multiple === 'always') return true;
    if (rule.multiple === 'never') return false;
    return Boolean(currentValue);
};

/** Human-readable summary of the submission policy, for hints and badges. */
export const describeSubmissionPolicy = (formType, allowMultiple) => {
    const rule = getFormTypeRule(formType);
    if (rule.multiple === 'always') return 'Multiple submissions (always)';
    if (rule.multiple === 'never') return 'Single submission only';
    return allowMultiple ? 'Multiple submissions allowed' : 'Single submission only';
};

// ── Fixed attendance fields ────────────────────────────────────────────────
//
// Every attendance-type template always carries these two fields, and they
// can't be edited or removed in the builder — only additional fields can be
// added alongside them. The ids are a fixed contract with the backend
// (PharmD_System/routers/forms.py submit_form): an attendance submission is
// read back out by these exact ids and mirrored into db.attendance_requests,
// which is what My Attendance / Student Attendance actually read from. Don't
// rename these ids without updating that backend extraction too.
export const ATTENDANCE_STUDY_TYPE_FIELD_ID = 'study_type';
export const ATTENDANCE_DAYS_FIELD_ID = 'attendance_days';

export const ATTENDANCE_FIXED_FIELD_IDS = [ATTENDANCE_STUDY_TYPE_FIELD_ID, ATTENDANCE_DAYS_FIELD_ID];

export const getAttendanceFixedFields = () => [
    {
        id: ATTENDANCE_STUDY_TYPE_FIELD_ID,
        type: 'select',
        label: 'Study Type',
        help_text: 'Full-time or part-time for the days being logged.',
        required: true,
        locked: true,
        options: [
            { value: 'full_time', label: 'Full-time' },
            { value: 'part_time', label: 'Part-time' }
        ]
    },
    {
        id: ATTENDANCE_DAYS_FIELD_ID,
        type: 'attendance_calendar',
        label: 'Attendance Days',
        help_text: 'Pick every day being logged, across any month or year — each day gets its own start/end time.',
        required: true,
        locked: true,
        options: []
    }
];

export const isAttendanceFixedFieldId = (fieldId) => ATTENDANCE_FIXED_FIELD_IDS.includes(fieldId);
